import { useCallback } from "react";
import { Settings } from "../settings";
import { COIN_SYMBOL_IDS } from "../SymbolData";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import store from "../State/Store/store";

/**
 * Hook for triggering landing/idle animations on reels.
 * Uses cached animation availability from OverlayReelController for optimized lookups.
 *
 * Turbo mode: plays idle-only animations (lightweight, seamless transition to win).
 * Normal/manual stop: plays full landing→idle sequence.
 */
export const useReelLandingAnimations = () => {
  const { reelRefs, overlayReelRefs } = useReelRefs();

  const triggerReelLandingAnimations = useCallback(
    (reelIndex, reelData, betOverride = null, skipRAF = false) => {
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];
      const overlayController = overlayReelRef?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();

      if (!overlayController || !reelController || !reelData) {
        return;
      }

      // Turbo mode: idle-only animations (lightweight, seamless win transitions)
      const isTurbo =
        store.getState().settings.autoPlaySpeed === "turbo";

      const textureHideOps = [];

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolId = reelData[symbolIndex];

        if (symbolId === undefined) {
          continue;
        }

        // Turbo with landing animations disabled: skip all spine work, show static textures
        if (isTurbo && !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) {
          if (reelController.showSymbolTexture) {
            reelController.showSymbolTexture(symbolIndex);
          }
          continue;
        }

        const { hasLanding, hasIdle } =
          overlayController.getAvailableAnimations(symbolIndex, symbolId);

        if (isTurbo) {
          // Turbo: idle-only (skips landing animation entirely)
          if (hasIdle) {
            overlayController.showIdleAnimation(
              symbolIndex,
              symbolId,
              betOverride,
            );
            textureHideOps.push({ reelController, symbolIndex });
          } else {
            if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
          }
        } else if (hasLanding || hasIdle) {
          // Normal/manual stop: full landing→idle sequence
          overlayController.showLandingAnimation(
            symbolIndex,
            symbolId,
            betOverride,
          );

          textureHideOps.push({ reelController, symbolIndex });
        } else {
          if (reelController.showSymbolTexture) {
            reelController.showSymbolTexture(symbolIndex);
          }
        }
      }

      if (!skipRAF && textureHideOps.length > 0) {
        requestAnimationFrame(() => {
          textureHideOps.forEach(({ reelController, symbolIndex }) => {
            if (reelController.hideSymbolTexture) {
              reelController.hideSymbolTexture(symbolIndex);
            }
          });
        });
      }

      return textureHideOps;
    },
    [
      overlayReelRefs,
      reelRefs,
    ],
  );

  return { triggerReelLandingAnimations };
};

/**
 * Hook for triggering idle animations.
 * Default mode: Shows idle animations for all symbols that have them.
 * Transition mode: Only transitions active win animations to idle.
 */
export const useInitialIdleAnimations = () => {
  const { reelRefs, overlayReelRefs, dimReelSetRef } = useReelRefs();

  const triggerInitialIdleAnimations = useCallback(
    (onlyTransitionWinAnimations = false) => {
      for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
        const overlayReelRef = overlayReelRefs.current?.[reelIndex];
        const overlayController = overlayReelRef?.getOverlayReelController();
        const reelController =
          reelRefs.current?.[reelIndex]?.getReelController();

        if (!overlayController || !reelController) {
          continue;
        }

        if (onlyTransitionWinAnimations) {
          overlayController.hideAllAnimations();
          
          if (overlayController.isExpandingWildVisible) {
            overlayController.forceStopExpandingWildFullReel?.();
          }

          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId,
            );

            if (hasIdle) {
              overlayController.showIdleAnimation(symbolIndex, symbolId);
            }
          }
        } else {
          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId,
            );

            if (hasIdle) {
              overlayController.showIdleAnimation(symbolIndex, symbolId);

              requestAnimationFrame(() => {
                if (reelController.hideSymbolTexture) {
                  reelController.hideSymbolTexture(symbolIndex);
                }
              });
            }
          }
        }
      }

      if (onlyTransitionWinAnimations && dimReelSetRef.current) {
        dimReelSetRef.current.undimReels();
      }
    },
    [overlayReelRefs, reelRefs, dimReelSetRef],
  );

  return { triggerInitialIdleAnimations };
};

/**
 * Hook for triggering landing animations for individual Hold & Win symbols.
 * Works on a per-symbol basis rather than per-reel.
 */
export const useHoldAndWinLandingAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinSymbolLandingAnimation = useCallback(
    (symbolIndex, reelIndex, symbolId, onTextureHiddenCallback) => {
      const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      const overlaySymbolController = overlaySymbolRef?.getController?.();

      const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
      const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);
      const symbolController = symbolRef?.current?.getSymbolController?.();

      if (!overlaySymbolController || !symbolController) {
        if (onTextureHiddenCallback) {
          onTextureHiddenCallback();
        }
        return;
      }

      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        const animationStarted = overlaySymbolController.showLandingAnimation(symbolId);

        if (animationStarted) {
          const spinningContainer = symbolController.spinningContainer;
          const staticCoinSprite = symbolController.staticCoinSprite;
          requestAnimationFrame(() => {
            const containerAlive = spinningContainer && !spinningContainer.destroyed;
            const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
            if (containerAlive || staticAlive) {
              symbolController.hideSymbolTexture();
            }

            if (onTextureHiddenCallback) {
              onTextureHiddenCallback();
            }
          });
        } else {
          if (onTextureHiddenCallback) {
            onTextureHiddenCallback();
          }
        }
      }
    },
    [holdAndWinReelRefs, holdAndWinOverlayReelRefs],
  );

  return { triggerHoldAndWinSymbolLandingAnimation };
};

/**
 * Hook for triggering idle animations for all Hold & Win symbols at once.
 * Used during Hold and Win reload to show idle animations for existing coins.
 */
export const useHoldAndWinIdleAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const overlaySymbolController = overlaySymbolRef?.getController?.();

        const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
        const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);

        if (!symbolRef || !symbolRef.current) {
          continue;
        }

        const symbolController = symbolRef.current.getSymbolController?.();

        if (!overlaySymbolController || !symbolController) {
          continue;
        }

        const symbolId = symbolController.symbolId;
        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          const animationStarted = overlaySymbolController.showIdleAnimation(symbolId);

          if (animationStarted) {
            const spinningContainer = symbolController.spinningContainer;
            const staticCoinSprite = symbolController.staticCoinSprite;
            requestAnimationFrame(() => {
              const containerAlive = spinningContainer && !spinningContainer.destroyed;
              const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
              if (containerAlive || staticAlive) {
                symbolController.hideSymbolTexture();
              }
            });
          }
        }
      }
    }
  }, [holdAndWinReelRefs, holdAndWinOverlayReelRefs]);

  return { triggerHoldAndWinIdleAnimations };
};

/**
 * Hook for triggering WIN animations for coins on base game overlay reels.
 * Used during Hold and Win intro reload to show win animations for trigger coins.
 */
export const useTriggerCoinWinAnimations = () => {
  const { reelRefs, overlayReelRefs, dimReelSetRef } = useReelRefs();

  const triggerCoinWinAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const reelRef = reelRefs.current?.[reelIndex];
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];

      if (!reelRef || !overlayReelRef) {
        continue;
      }

      const reelController = reelRef.getReelController?.();
      const overlayReelController = overlayReelRef.getOverlayReelController?.();

      if (!reelController || !overlayReelController) {
        continue;
      }

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbol = reelController.symbolsContainer?.children?.[symbolIndex];

        if (!symbol) {
          continue;
        }

        const symbolId = symbol.currentSymbolId || symbol.symbolId;

        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          overlayReelController.showWinAnimation(symbolIndex, symbolId);

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }
      }
    }

    if (dimReelSetRef.current) {
      dimReelSetRef.current.dimReels();
    }
  }, [reelRefs, overlayReelRefs, dimReelSetRef]);

  return { triggerCoinWinAnimations };
};

/**
 * Hook for triggering dark animations for all Hold & Win symbols at once.
 * Used during Hold and Win completion reload to show collected state (dark coins).
 */
export const useHoldAndWinCompletionIdleAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinCompletionIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const overlaySymbolController = overlaySymbolRef?.getController?.();

        const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
        const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);

        if (!symbolRef || !symbolRef.current) {
          continue;
        }

        const symbolController = symbolRef.current.getSymbolController?.();

        if (!overlaySymbolController || !symbolController) {
          continue;
        }

        const symbolId = symbolController.symbolId;

        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          const animationStarted = overlaySymbolController.showDarkAnimation(symbolId);

          if (animationStarted) {
            const spinningContainer = symbolController.spinningContainer;
            const staticCoinSprite = symbolController.staticCoinSprite;
            requestAnimationFrame(() => {
              const containerAlive = spinningContainer && !spinningContainer.destroyed;
              const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
              if (containerAlive || staticAlive) {
                symbolController.hideSymbolTexture();
              }
            });
          }
        } else {
          if (symbolController.showSymbolTexture) {
            symbolController.showSymbolTexture();
          }
        }
      }
    }
  }, [holdAndWinReelRefs, holdAndWinOverlayReelRefs]);

  return { triggerHoldAndWinCompletionIdleAnimations };
};
